import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { generateRequestKey } from '@/utils/request/request-helpers'
import { cacheManager } from '@/utils/request/request-cache'
import { getToken } from '@/utils/request/token'
import { setupApiMetrics } from '@/utils/metrics/api-perf'
import { isUnknownRecord } from '@/types/json'
import { setupInterceptors } from './request-interceptors'
import type { RequestConfig } from './request-config'

type CacheScope = NonNullable<RequestConfig['cache']>['scope']

interface TokenCachePayload {
  /** JWT 所属终端，用于区分 user/admin 等登录端 */
  terminal?: unknown
  /** JWT 主体 ID，用于区分同浏览器切换账号 */
  subject_id?: unknown
  /** JWT 版本号，用于密码重置或登出后隔离旧缓存 */
  token_version?: unknown
}

/** 解码 JWT payload 的 base64url 字段。 */
const decodeBase64Url = (value: string): string => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const normalizedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')

  return window.atob(normalizedBase64)
}

/** 解析 JWT payload，失败时返回 null。 */
const parseTokenPayload = (token: string): TokenCachePayload | null => {
  const [, payload] = token.split('.')

  if (!payload) return null

  try {
    const parsed = JSON.parse(decodeBase64Url(payload)) as unknown

    if (!isUnknownRecord(parsed)) return null

    return {
      ...('terminal' in parsed ? { terminal: parsed['terminal'] } : {}),
      ...('subject_id' in parsed ? { subject_id: parsed['subject_id'] } : {}),
      ...('token_version' in parsed ? { token_version: parsed['token_version'] } : {}),
    }
  } catch {
    return null
  }
}

/** 为无法解析的 token 生成短哈希，避免把完整 token 放入缓存 key。 */
const hashToken = (token: string): string => {
  let hash = 0

  for (let index = 0; index < token.length; index += 1) {
    hash = (hash * 31 + token.charCodeAt(index)) >>> 0
  }

  return hash.toString(36)
}

/** 获取 auth 缓存命名空间，优先使用后端 token 主体信息。 */
const getAuthCacheNamespace = (): string => {
  const token = getToken()

  if (!token) return 'auth:anonymous'

  const payload = parseTokenPayload(token)

  if (
    payload?.terminal !== undefined &&
    payload.subject_id !== undefined &&
    payload.token_version !== undefined
  ) {
    return `auth:${String(payload.terminal)}:${String(payload.subject_id)}:${String(
      payload.token_version
    )}`
  }

  return `auth:${hashToken(token)}`
}

/** 根据请求配置生成带 public/auth 隔离的 GET 缓存 key。 */
const generateScopedCacheKey = (url: string, config: RequestConfig): string => {
  const scope: CacheScope = config.cache?.scope || (config.skipAuth ? 'public' : 'auth')
  const namespace = scope === 'public' ? 'public' : getAuthCacheNamespace()

  return `${namespace}:${generateRequestKey(url, 'GET', config.params)}`
}

export class Request {
  private instance: AxiosInstance
  private enableTokenRefresh: boolean

  /** 暴露底层 axios 实例，供性能监控等外部模块挂载拦截器 */
  get axiosInstance(): AxiosInstance {
    return this.instance
  }

  constructor(config: AxiosRequestConfig, enableTokenRefresh = true) {
    this.instance = axios.create(config)
    this.enableTokenRefresh = enableTokenRefresh
    setupApiMetrics(this.instance)
    setupInterceptors(this.instance, this.enableTokenRefresh)
  }

  async get<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
    if (config?.cache?.enable) {
      const cacheKey = generateScopedCacheKey(url, config)
      if (cacheManager.has(cacheKey)) {
        const cached = cacheManager.get(cacheKey)
        if (cached !== null) {
          return cached as T
        }
      }

      const pendingRequest = cacheManager.getPending(cacheKey)
      if (pendingRequest) return pendingRequest as Promise<T>

      const request = this.instance.get<T, T>(url, config).then(result => {
        cacheManager.set(cacheKey, result, config.cache?.ttl)
        return result
      })

      return cacheManager.setPending(cacheKey, request) as Promise<T>
    }
    return this.instance.get<T, T>(url, config)
  }

  post<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.instance.post<T, T>(url, data, config)
  }

  put<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.instance.put<T, T>(url, data, config)
  }

  patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.instance.patch<T, T>(url, data, config)
  }

  delete<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
    return this.instance.delete<T, T>(url, config)
  }

  request<T = unknown>(config: RequestConfig): Promise<T> {
    return this.instance.request<T, T>(config)
  }
}
